import { addDays, format, formatISO } from 'date-fns'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { EmployerOnboardingPage, useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { FormRadioGroupDS } from 'components/reusable/form/FormFields'
import { CenteredLayout } from 'pages/employer_onboarding/CenteredLayout'
import { useEmployer } from 'pages/employer_onboarding/EmployerProvider'
import { Button, InputBlock, StepNav, usePageTitle } from 'slab'
import {
  usePatchQuotingProspectsProspectIdQuotesId as useUpdateQuote,
  usePutQuotingProspectsId,
} from 'utils/web_api'

import { useQuote } from '../QuoteProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { emitEmployerOnboardingEvent } from '../utils'
import { useProspect } from './ProspectProvider'
import { useFlags } from '../FlagProvider'

const PLAN_DATA_YEAR_ERROR_MESSAGE = "We can't generate quotes for next year at the moment."

// TODO: Check with the grammar police on our opinion on how to pluralize names
// ending with an "s". This current implementation is fairly naive.
function makePossessive (name?: string) {
  if (!name) return 'your'

  return `${name}'s`
}

function getDefaultMonth (isAfterOctober15: boolean) {
  const now = new Date()

  // If after October 15th UTC, default to January of next year
  if (isAfterOctober15) {
    return 0 // January (0-indexed)
  }

  if (now.getDate() < 15) {
    return now.getMonth() + 1
  }

  return now.getMonth() + 2
}

function getValidMonths (year?: number) {
  const now = new Date()
  let firstValidMonth = 0

  if (year === now.getFullYear()) {
    firstValidMonth = now.getMonth() + 1
  }

  // The values here match the month argument of the `Date` constructor, which
  // is bizarrely zero-indexed, so 0 is January. ¯\_(ツ)_/¯
  const months = [
    { value: 0, label: 'January 1st', disabled: false },
    { value: 1, label: 'February 1st', disabled: false },
    { value: 2, label: 'March 1st', disabled: false },
    { value: 3, label: 'April 1st', disabled: false },
    { value: 4, label: 'May 1st', disabled: false },
    { value: 5, label: 'June 1st', disabled: false },
    { value: 6, label: 'July 1st', disabled: false },
    { value: 7, label: 'August 1st', disabled: false },
    { value: 8, label: 'September 1st', disabled: false },
    { value: 9, label: 'October 1st', disabled: false },
    { value: 10, label: 'November 1st', disabled: false },
    { value: 11, label: 'December 1st', disabled: false },
  ]

  return months.map(month => ({
    ...month,
    disabled: month.value < firstValidMonth,
  }))
}

export function BenefitsPage () {
  usePageTitle('Current benefits')

  const { flags } = useFlags()
  const { employer } = useEmployer()
  const { quote, refetch: refetchQuote } = useQuote()
  const { prospect, refetch: refetchProspect } = useProspect()
  const { isEmbedded } = useEmbeddedOnboarding()
  const { navigateToPreviousStep, navigateToStep } = useOnboardingStep()

  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not update your current benefits because of an error',
  })

  const now = new Date()
  const currentMonth = now.getMonth() // 0-indexed
  const currentDate = now.getDate()
  const currentYear = now.getFullYear()
  const isAfterOctober15 = currentMonth > 9 || (currentMonth === 9 && currentDate > 15)
  const canSelectCurrentYear = flags?.enableCurrentYearStartDates || !isAfterOctober15

  const form = useForm({
    defaultValues: {
      offers_health_insurance_today: prospect?.offers_health_insurance_today
        ? 'yes'
        : 'no',
      month: quote?.coverage_start_date
        ? new Date(quote.coverage_start_date).getUTCMonth()
        : getDefaultMonth(isAfterOctober15),
      year: quote?.coverage_start_date
        ? new Date(quote.coverage_start_date).getUTCFullYear()
        : isAfterOctober15
          ? currentYear + 1
          : currentYear,
    },
    mode: 'onTouched',
  })

  const updateQuoteMutation = useUpdateQuote()
  const updateProspectMutation = usePutQuotingProspectsId()

  const selectedYear = form.watch('year')
  const selectedMonth = form.watch('month')

  const shouldShowPendingPricesWarning = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() // 0-indexed
    const currentDate = new Date().getDate()
    const isNextYear = selectedYear === currentYear + 1
    const isBeforeNovember = currentMonth < 10 || (currentMonth === 10 && currentDate < 1)
    return isNextYear && isBeforeNovember
  }, [selectedYear])

  useEffect(() => {
    const currentYear = new Date().getFullYear()
    if (selectedYear === currentYear + 1) {
      window.segment?.track(`[ER] ${selectedYear} coverage start date selected`)
    }
  }, [selectedYear])

  const coverageStartDate = new Date(selectedYear, selectedMonth, 1)
  const employeeSelectionDate = addDays(coverageStartDate, -3)

  const planSelectionWindowName = useMemo(() => {
    const today = new Date()
    const oneMonthFromToday = addDays(today, 30)
    const isWithinOneMonth = employeeSelectionDate <= oneMonthFromToday

    if (isWithinOneMonth) {
      return `Today - ${format(employeeSelectionDate, 'MMMM d')}`
    } else if (coverageStartDate.getMonth() === 0 && coverageStartDate.getDate() === 1) {
      return 'November 5 - November 30'
    } else {
      const oneMonthBeforeCoverage = new Date(coverageStartDate.getFullYear(), coverageStartDate.getMonth() - 1, 1)
      return `${format(oneMonthBeforeCoverage, 'MMMM d')} - ${format(employeeSelectionDate, 'MMMM d')}`
    }
  }, [coverageStartDate, employeeSelectionDate])

  async function onSubmit () {
    if (!prospect) return

    const data = form.getValues()
    const coverageStartDate = formatISO(new Date(data.year!, data.month!, 1), {
      representation: 'date',
    })

    try {
      await updateProspectMutation.mutateAsync({
        id: prospect.id,
        data: {
          offers_health_insurance_today: data.offers_health_insurance_today === 'yes',
        },
      })
      await refetchProspect()

      await updateQuoteMutation.mutateAsync({
        id: quote!.id,
        prospectId: prospect.id,
        data: {
          coverage_start_date: coverageStartDate,
        },
      })

      emitEmployerOnboardingEvent('[ER] Has current benefits choice added', {
        offers_benefits: data.offers_health_insurance_today === 'yes'
      })

      await refetchQuote()

      if (shouldShowPendingPricesWarning) {
        navigateToStep(EmployerOnboardingPage.PENDING_RATES)
      } else if (data.offers_health_insurance_today === 'yes') {
        navigateToStep(EmployerOnboardingPage.CURRENT_BENEFITS)
      } else {
        navigateToStep(isEmbedded ? EmployerOnboardingPage.ALLOWANCE_STRATEGY : EmployerOnboardingPage.NEW_BENEFITS)
      }
    } catch (error) {
      setError(error)
    }
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading={`Tell us about ${makePossessive(
          employer?.name
        )} current benefits situation`}
        subheading='Insurance companies require that coverage starts on the 1st of the month.'
      />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container>
          <div className='flex flex-col lg:flex-row gap-10 items-start'>
            <div className='flex-1 space-y-6'>
              <InputBlock label='Desired coverage start date'>
                <InputBlock.Row>
                  <InputBlock.Select
                    {...form.register('month', {
                      required: 'Please select a month.',
                      valueAsNumber: true,
                      validate: (value) => {
                        const year = form.getValues('year')

                        if (value !== undefined && value <= currentMonth && year === currentYear) {
                          return 'Coverage start date must be in the future.'
                        }

                        return true
                      },
                    })}
                    label='Date'
                    error={form.formState.errors.month?.message}
                  >
                    {getValidMonths(selectedYear).map(
                      ({ value, label, disabled }) => (
                        <option key={value} value={value} disabled={disabled}>
                          {label}
                        </option>
                      )
                    )}
                  </InputBlock.Select>
                  <InputBlock.Select
                    {...form.register('year', {
                      required: 'Please select a year.',
                      valueAsNumber: true,
                      validate: (value) => {
                        form.trigger('month')

                        const maxAllowedYear = flags?.enable2026Plans ? currentYear + 1 : currentYear

                        if (value && (value < currentYear || value > maxAllowedYear)) {
                          return PLAN_DATA_YEAR_ERROR_MESSAGE
                        }

                        // If after October 15th and the flag is not enabled, don't allow current year
                        if (value && value === currentYear && !canSelectCurrentYear) {
                          return `Only ${currentYear + 1} start dates are available at this time.`
                        }

                        return true
                      },
                    })}
                    label='Year'
                    error={form.formState.errors.year?.message}
                  >
                    {(() => {
                      return [
                        <option
                          key={currentYear}
                          value={currentYear.toString()}
                          disabled={!canSelectCurrentYear}
                        >
                          {currentYear}
                        </option>,
                        <option key={currentYear + 1} value={(currentYear + 1).toString()}>{currentYear + 1}</option>
                      ]
                    })()}
                  </InputBlock.Select>
                </InputBlock.Row>
              </InputBlock>
              <FormRadioGroupDS
                control={form.control}
                name='offers_health_insurance_today'
                label={`Does ${employer?.name} currently offer health insurance?`}
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              {errorBanner}
            </div>
            <div className='w-full lg:w-80 flex-shrink-0'>
              <StepNav className='border border-gray-200 rounded-lg p-4 [&_.w-px]:bg-thatch-500 [&_.text-gray-700]:text-thatch-500 [&_.pl-3]:pl-0.5 [&_ul.mt-3]:mt-0 [&_ul.mt-3]:pb-4'>
                <h3 className='body mb-4'>Your expected timeline:</h3>
                <StepNav.Step
                  name='Today'
                  active
                >
                  <span className='text-subdued'>
                    Finish setting up and invite your employees
                  </span>
                </StepNav.Step>
                <StepNav.Step
                  name={planSelectionWindowName}
                  active
                >
                  <span className='text-subdued'>
                    Employees pick their plans
                  </span>
                </StepNav.Step>
                <StepNav.Step
                  name={`${format(coverageStartDate, 'MMMM d')}`}
                  active
                  hideLine
                >
                  <span className='text-subdued ml-3'>
                    Coverage starts
                  </span>
                </StepNav.Step>
              </StepNav>
            </div>
          </div>
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button
            variant='secondary'
            onClick={navigateToPreviousStep}
          >
            Back
          </Button>
          <Button
            disabled={form.formState.isSubmitting}
            type='submit'
            variant='primary'
            rightIcon={ArrowRightIcon}
          >
            Next step
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
